import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { LyricSyncState } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../common/types';
import { fullVideoMessages } from './fullVideoMessages';
import type { SongLyricsView } from '../../../smartLinks/catalog/song/page/catalogSongPath';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricSyncNoticeProps {
  state: LyricSyncState;
  /** Opens the Lyrics section of the song page in Catalog, where the times are fixed. */
  onOpenSongLyrics: (view?: SongLyricsView) => void;
}

/**
 * The lyric sync boxes of step 2 (design s7c, second row): "The lyrics are syncing" (the video
 * waits for the times) and "We could not sync the lyrics" (make it with the times set by hand, or
 * fix them). Nothing for a song that is synced or has no lyrics.
 */
const LyricSyncNoticeComponent: React.FC<LyricSyncNoticeProps> = ({ state, onOpenSongLyrics }) => {
  const intl = useIntl();

  if (state === 'syncing') {
    return (
      <div className={styles.syncBox} data-testid="studio-sync-syncing">
        <span className={clsx(styles.chip, styles.chipRun)}>{intl.formatMessage(fullVideoMessages.syncingChip)}</span>
        <b>{intl.formatMessage(fullVideoMessages.syncingTitle)}</b>
        <span>{intl.formatMessage(fullVideoMessages.syncingBody)}</span>
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className={styles.syncBox} data-testid="studio-sync-failed">
        <span className={clsx(styles.chip, styles.chipBad)}>{intl.formatMessage(fullVideoMessages.failedChip)}</span>
        <b>{intl.formatMessage(fullVideoMessages.failedTitle)}</b>
        <span>{intl.formatMessage(fullVideoMessages.failedBody)}</span>
        <button type="button" className="btn btn-link btn-sm p-0 align-self-start" onClick={() => onOpenSongLyrics()}>
          {intl.formatMessage(fullVideoMessages.fixTimes)}
        </button>
      </div>
    );
  }
  return null;
};

export const LyricSyncNotice = TypedMemo(LyricSyncNoticeComponent);
